import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  type OnDestroy,
} from '@angular/core';
import {
  ChartFacade,
  ChartId,
} from '@principle-theorem/ng-clinical-charting/store';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  ClinicalChart,
  isSameTimestamp,
} from '@principle-theorem/principle-core';
import {
  ChartType,
  IChartHistoryItem,
  type IClinicalChart,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  getEnumValues,
  isWithRef,
  snapshot,
} from '@principle-theorem/shared';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, takeUntil, withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'pr-charting-history-list',
  templateUrl: './charting-history-list.component.html',
  styleUrls: ['./charting-history-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChartingHistoryListComponent implements OnDestroy {
  private _onDestroy$: Subject<void> = new Subject();
  loading$ = new BehaviorSubject<boolean>(false);
  selectedItem$ = new BehaviorSubject<IChartHistoryItem | undefined>(undefined);
  items$ = new ReplaySubject<IChartHistoryItem[]>(1);
  chartType$ = new ReplaySubject<ChartType>(1);
  selectedChartTypeIndex$: Observable<number>;
  isOutOfSync$: Observable<boolean>;
  chartTypeOption = ChartType;
  @Output() chartSelected = new EventEmitter<IClinicalChart>();
  @Output() addNewChart = new EventEmitter<void>();
  @Output() chartTypeSelected = new EventEmitter<ChartType>();

  @Input()
  set items(items: IChartHistoryItem[]) {
    if (items) {
      this.items$.next(items);
    }
  }

  @Input()
  set chartType(chartType: ChartType) {
    if (chartType) {
      this.chartType$.next(chartType);
    }
  }

  constructor(
    private _chartState: ChartFacade,
    private _organisation: OrganisationService
  ) {
    this.items$
      .pipe(
        withLatestFrom(this.selectedItem$, this.chartType$),
        map(([items, selectedItem, chartType]) =>
          this._getItem(items, chartType, selectedItem)
        ),
        filterUndefined(),
        takeUntil(this._onDestroy$)
      )
      .subscribe((item) => this.selectChartHistory(item));

    this.selectedChartTypeIndex$ = this.chartType$.pipe(
      map((chartType) => getEnumValues(ChartType).indexOf(chartType))
    );

    this.isOutOfSync$ = this._chartState.isOutOfSync$(ChartId.InAppointment);
  }

  selectChartHistory(selected: IChartHistoryItem | undefined): void {
    this.selectedItem$.next(selected);
    this.chartSelected.emit(selected?.value);
  }

  addFirstChart(): void {
    this.loading$.next(true);
    this.addNewChart.emit();
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async syncChart(): Promise<void> {
    const [selected, storeChart, staffer] = await Promise.all([
      snapshot(this.selectedItem$),
      snapshot(this._chartState.clinicalChartState$(ChartId.InAppointment)),
      snapshot(this._organisation.staffer$),
    ]);

    if (!selected) {
      return;
    }

    if (isWithRef(storeChart)) {
      await ClinicalChart.archive(storeChart, staffer?.ref);
    }

    const currentChart = await Firestore.getDoc(selected.value.ref);
    this._chartState.setChart(ChartId.InAppointment, currentChart);
  }

  setChartType(index: number): void {
    const type = index === 0 ? ChartType.Clinical : ChartType.Periodontal;
    this.chartTypeSelected.emit(type);
  }

  private _getItem(
    items: IChartHistoryItem[],
    chartType: ChartType,
    selectedItem?: IChartHistoryItem
  ): IChartHistoryItem | undefined {
    if (!items.length) {
      return;
    }

    if (chartType === ChartType.Periodontal) {
      const missingPerioRecords =
        selectedItem && !selectedItem.value.perioRecords;
      if (missingPerioRecords) {
        return items[0];
      }
    }

    if (!selectedItem) {
      return items[0];
    }

    const selectedExistsInItems = items.some((item) =>
      isSameTimestamp(item.timestamp, selectedItem?.timestamp)
    );
    return !selectedExistsInItems ? items[0] : undefined;
  }
}
